import axios, {
  isAxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@/lib/env';

import { normalizeApiError } from './errors';

/** The versioned REST prefix every endpoint lives under. */
const API_VERSION_PREFIX = '/v1';

/** The refresh endpoint, matched so it never triggers a refresh of itself. */
const REFRESH_PATH = '/auth/refresh';

/**
 * A retried request carries this flag on its config, so a request that 401s a
 * second time (after a refresh that appeared to succeed) falls through to the
 * error instead of looping forever.
 */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _atlasRetried?: boolean;
}

/**
 * What runs when the session is gone for good — a failed refresh.
 *
 * Registered by the auth layer (`registerSessionExpiredHandler`) rather than
 * imported, because this module sits below the React tree and cannot reach the
 * `QueryClient`. Clearing the session query is what flips the app back to the
 * sign-in screen (spec R7.5).
 */
type SessionExpiredHandler = () => void;

let sessionExpiredHandler: SessionExpiredHandler | null = null;

export const registerSessionExpiredHandler = (
  handler: SessionExpiredHandler | null,
): void => {
  sessionExpiredHandler = handler;
};

/**
 * A single in-flight refresh, shared by every 401 that arrives while it runs.
 *
 * Without this a burst of parallel reads all 401ing at once would each fire
 * their own refresh; with it they all await the same one and then replay.
 */
let refreshInFlight: Promise<void> | null = null;

const refreshOnce = (): Promise<void> => {
  refreshInFlight ??= apiClient
    .post(REFRESH_PATH)
    .then(() => undefined)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

const baseURL = `${env.VITE_API_URL.replace(/\/+$/, '')}${API_VERSION_PREFIX}`;

/**
 * The single axios instance for the Atlas API.
 *
 * `withCredentials` is on from Phase 1 even though nothing is authenticated
 * yet: Phase 2 puts the session in an httpOnly cookie, and turning it on later
 * is the kind of change that works locally and fails the moment the app and the
 * API are on different hosts.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    Accept: 'application/json',
  },
});

/*
 * Normalise every rejection to an `ApiError` before it reaches React Query.
 *
 * Everything downstream — query `error` objects, mutation `onError`, the route
 * error boundary, toasts — can then rely on `error.message` already being a
 * readable English sentence, and on `error.status` when it needs to branch.
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    /*
     * ------------------------------------------------------------------
     * PHASE 2 SEAM — refresh-on-401 (spec R7.5).
     *
     * A 401 on any request other than the refresh call itself gets exactly one
     * refresh attempt, de-duplicated across parallel 401s by `refreshOnce`. On
     * success the original request is replayed once (guarded by the
     * `_atlasRetried` flag so a still-401 replay cannot loop). On failure the
     * session handler clears client state — which returns the user to the
     * sign-in screen — and the original rejection continues to the
     * normalisation below ("Your session has expired. Sign in again.").
     * ------------------------------------------------------------------
     */
    if (isAxiosError(error) && error.response?.status === 401) {
      const config = error.config as RetriableConfig | undefined;
      const isRefreshCall = (config?.url ?? '').includes(REFRESH_PATH);

      if (config !== undefined && !isRefreshCall && config._atlasRetried !== true) {
        config._atlasRetried = true;

        try {
          await refreshOnce();
        } catch {
          sessionExpiredHandler?.();
          return Promise.reject(normalizeApiError(error));
        }

        return apiClient(config);
      }
    }

    return Promise.reject(normalizeApiError(error));
  },
);
